import { Inbox, type LucideIcon } from 'lucide-react'
import { isValidElement, type ReactNode } from 'react'
import { cn } from './cn'
import { NeuralPattern } from './NeuralPattern'

export interface EmptyStateProps {
  /** A lucide icon component (for example `MessageSquare`) or any element. */
  icon?: LucideIcon | ReactNode
  title: ReactNode
  description?: ReactNode
  /** Optional action, usually a <Button>. */
  action?: ReactNode
  /** card = bordered surface panel (default), plain = no chrome. */
  variant?: 'card' | 'plain'
  /** Circle and icon colours. */
  tone?: 'navy' | 'danger'
  className?: string
}

function renderIcon(icon: EmptyStateProps['icon']) {
  if (icon == null) return <Inbox strokeWidth={1.75} />
  if (isValidElement(icon)) return icon
  if (typeof icon === 'function' || (typeof icon === 'object' && icon !== null && '$$typeof' in icon)) {
    const Icon = icon as LucideIcon
    return <Icon strokeWidth={1.75} />
  }
  return icon as ReactNode
}

/** Friendly empty list or page state over a very faint (4 percent) neural pattern. */
export function EmptyState({ icon, title, description, action, variant = 'card', tone = 'navy', className }: EmptyStateProps) {
  return (
    <div
      className={cn(
        'relative isolate flex flex-col items-center overflow-hidden px-6 py-12 text-center',
        variant === 'card' && 'rounded-card border border-line bg-surface',
        className,
      )}
    >
      <NeuralPattern opacity={0.04} className="-z-10 text-text" fade />
      <div
        className={cn(
          'mb-4 flex size-12 items-center justify-center rounded-full [&_svg]:size-6',
          tone === 'danger' ? 'bg-danger-tint text-danger' : 'bg-accent-tint text-accent-strong',
        )}
        aria-hidden="true"
      >
        {renderIcon(icon)}
      </div>
      <h3 className="text-section text-text">{title}</h3>
      {description && typeof description === 'string' ? (
        <p className="mt-1.5 max-w-[360px] text-body text-muted">{description}</p>
      ) : description ? (
        // Markup may hold block elements: a div keeps it valid HTML (no div inside a p, Q-COMM-35).
        <div className="mt-1.5 max-w-[360px] text-body text-muted">{description}</div>
      ) : null}
      {action && <div className="mt-5 flex flex-wrap items-center justify-center gap-2">{action}</div>}
    </div>
  )
}
